import type { DeadlineFilter, ItemSort } from "@egalregal/shared";
import { readValues, writeValues } from "../../shared/lib/urlParams";

/**
 * The item list's shareable filter state (`/items?owner&tag&category&sort&photos&deadline`).
 * Owner/tag/category/deadline are repeated params; unknown values are dropped
 * or replaced with defaults so a stale link never breaks the list. An empty
 * owner value is the "Unassigned" selection and is preserved.
 */
export interface ItemFilterState {
  owners: string[];
  tags: string[];
  categories: string[];
  sort: ItemSort;
  showPhotos: boolean;
  deadline: DeadlineFilter[];
}

export const DEFAULT_ITEM_FILTERS: ItemFilterState = {
  owners: [],
  tags: [],
  categories: [],
  sort: "name",
  showPhotos: true,
  deadline: [],
};

const ITEM_SORTS: readonly ItemSort[] = ["name", "newest", "category", "soonest"];
const DEADLINES: readonly DeadlineFilter[] = ["overdue", "soon"];

/**
 * Read the item filter state from a query string.
 *
 * @param params the URL search params.
 * @returns validated filters; unknown sort/deadline values fall back safely.
 */
export function readItemFilters(params: URLSearchParams): ItemFilterState {
  const sort = params.get("sort");
  return {
    owners: readValues(params, "owner"),
    tags: readValues(params, "tag").filter(Boolean),
    categories: readValues(params, "category").filter(Boolean),
    sort: isItemSort(sort) ? sort : DEFAULT_ITEM_FILTERS.sort,
    // Only the documented `hide` value is meaningful; anything else shows photos.
    showPhotos: params.get("photos") !== "hide",
    deadline: params.getAll("deadline").filter(isDeadline),
  };
}

/**
 * Write the item filter state onto a copy of the current params, removing
 * defaults and preserving unrelated parameters.
 *
 * @param current the existing URL params.
 * @param state the filters to encode.
 * @returns a new {@link URLSearchParams} safe to navigate to.
 */
export function writeItemFilters(
  current: URLSearchParams,
  state: ItemFilterState,
): URLSearchParams {
  const params = new URLSearchParams(current);
  writeValues(params, "owner", state.owners);
  writeValues(params, "tag", state.tags);
  writeValues(params, "category", state.categories);
  if (state.sort === DEFAULT_ITEM_FILTERS.sort) params.delete("sort");
  else params.set("sort", state.sort);
  if (state.showPhotos) params.delete("photos");
  else params.set("photos", "hide");
  writeValues(params, "deadline", state.deadline);
  return params;
}

function isItemSort(value: string | null): value is ItemSort {
  return value !== null && (ITEM_SORTS as readonly string[]).includes(value);
}

function isDeadline(value: string): value is DeadlineFilter {
  return (DEADLINES as readonly string[]).includes(value);
}
